import type { RecentJournalFeedEntry } from "../../../api/WarehouseApi";
import { isRestoreComment } from "../../../domain/journal";
import type { RealtimeFeedItem } from "../../../stores/RealtimeStore";
import type { IDate } from "../../../types/date";
import { Action } from "../../../types/entities";
import {
  getDashboardProductJournalActionTitle,
  getDashboardProductJournalActionTone,
} from "../../shared/use-cases/journal-action-presentation";

const QUANTITY_FORMATTER = new Intl.NumberFormat("ru-RU", {
  maximumFractionDigits: 3,
});

export const DASHBOARD_EVENTS_LIMIT = 12;

function toIsoString(date: IDate | null): string {
  if (!date) {
    return new Date().toISOString();
  }

  const jsDate = new Date(
    date.year,
    Number(date.month) - 1,
    Number(date.day),
    Number(date.hour),
    Number(date.minute),
    0,
    0,
  );

  if (Number.isNaN(jsDate.getTime())) {
    return new Date().toISOString();
  }

  return jsDate.toISOString();
}

function formatQuantity(value: number): string {
  if (!Number.isFinite(value)) {
    return "1";
  }

  return QUANTITY_FORMATTER.format(value);
}

function resolveProductLabel(name: string | undefined, id: string): string {
  const normalizedName = name?.trim() || "";
  if (normalizedName) {
    return normalizedName;
  }

  const normalizedId = id.trim();
  if (normalizedId) {
    return `неизвестный товар (${normalizedId})`;
  }

  return "неизвестный товар";
}

function isBoxEntityId(entityId: string): boolean {
  return entityId.trim().startsWith("box_");
}

function resolveBoxLabel(name: string | undefined, id: string): string {
  const normalizedName = name?.trim() || "";
  if (normalizedName) {
    return normalizedName;
  }

  const normalizedId = id.trim();
  if (normalizedId) {
    return `неизвестная коробка (${normalizedId})`;
  }

  return "неизвестная коробка";
}

function isUserVisibleEvent(event: RealtimeFeedItem): boolean {
  if (event.topic === "ws.system") {
    return false;
  }

  if (event.type.startsWith("ws.")) {
    return false;
  }

  return event.sourceType === "user";
}

export function mapJournalEntryToFeedItem(
  entry: RecentJournalFeedEntry,
): RealtimeFeedItem {
  if (entry.eventType === "box.restored") {
    const boxId = entry.boxId.trim();
    const boxLabel = resolveBoxLabel(entry.boxName || undefined, boxId);
    const descriptionLines = [`Коробка: ${boxLabel}`];
    const comment = entry.comment?.trim();

    if (comment) {
      descriptionLines.push(`Комментарий: ${comment}`);
    }

    descriptionLines.push(
      `Источник: ${
        entry.actorType === "system"
          ? "системное событие"
          : "действие пользователя"
      }`,
    );
    descriptionLines.push(`Автор: ${entry.actorLabel}`);

    return {
      id: `journal_${entry.id}`,
      type: "journal.box.restored",
      topic: "journal.history",
      timestamp: toIsoString(entry.date),
      title: "Коробка восстановлена",
      description: descriptionLines.join("\n"),
      tone: "info",
      sourceType: entry.actorType,
      boxId: boxId || undefined,
    };
  }

  const action = entry.action || Action.put;
  const productId = entry.productId.trim();
  const boxId = entry.fromTo.trim();
  const boxEntity = isBoxEntityId(productId);
  const entityLabel = boxEntity
    ? resolveBoxLabel(entry.productName || undefined, productId)
    : resolveProductLabel(entry.productName || undefined, productId);
  const boxLabel = resolveBoxLabel(entry.boxName || undefined, boxId);
  const title = boxEntity
    ? action === Action.take
      ? "Коробка извлечена из родительской коробки"
      : action === Action.destroy
        ? "Коробка списана"
        : action === Action.put && isRestoreComment(entry.comment)
          ? "Коробка восстановлена"
          : action === Action.put
            ? "Коробка помещена в родительскую коробку"
            : "Операция с коробкой"
    : action === Action.put && isRestoreComment(entry.comment)
      ? "Товар восстановлен"
      : getDashboardProductJournalActionTitle(action);

  const descriptionLines = boxEntity
    ? [`Коробка: ${entityLabel}`, `Родительская коробка: ${boxLabel}`]
    : [
        `Товар: ${entityLabel}`,
        `Коробка: ${boxLabel}`,
        `Количество: ${formatQuantity(entry.value)}`,
      ];
  const comment = entry.comment?.trim();
  if (comment) {
    descriptionLines.push(`Комментарий: ${comment}`);
  }
  descriptionLines.push(
    `Источник: ${
      entry.actorType === "system"
        ? "системное событие"
        : "действие пользователя"
    }`,
  );
  descriptionLines.push(`Автор: ${entry.actorLabel}`);

  return {
    id: `journal_${entry.id}`,
    type: `journal.${action}`,
    topic: "journal.history",
    timestamp: toIsoString(entry.date),
    title,
    description: descriptionLines.join("\n"),
    tone: getDashboardProductJournalActionTone(action),
    sourceType: entry.actorType,
    productId: boxEntity ? undefined : productId || undefined,
    boxId: (boxEntity ? productId : boxId) || undefined,
  };
}

export function buildDashboardEvents(input: {
  realtimeEvents: RealtimeFeedItem[];
  journalEvents: RealtimeFeedItem[];
  showSystemEvents: boolean;
  limit?: number;
}): RealtimeFeedItem[] {
  const realtimeEvents = input.realtimeEvents.filter((event) =>
    input.showSystemEvents ? true : isUserVisibleEvent(event),
  );
  const combined = [...realtimeEvents, ...input.journalEvents];
  combined.sort((left, right) => {
    const leftTime = Date.parse(left.timestamp);
    const rightTime = Date.parse(right.timestamp);
    return (
      (Number.isNaN(rightTime) ? 0 : rightTime) -
      (Number.isNaN(leftTime) ? 0 : leftTime)
    );
  });

  return combined.slice(0, input.limit || DASHBOARD_EVENTS_LIMIT);
}

export function isDeletedEntityEvent(event: RealtimeFeedItem): boolean {
  if (event.type === "journal.destroy") {
    return true;
  }

  return (
    event.operation === "deleted" &&
    (event.entity === "products" || event.entity === "boxes")
  );
}
